// import classNames from "classnames";
import {
	BoltOutlined,
	CheckCircleOutlined,
	KeyboardDoubleArrowRightOutlined,
	QuestionMarkRounded,
} from "@mui/icons-material";
import { Dispatch, SetStateAction, useState } from "react";
import {
	IconTextList,
	PageSectionProps,
} from "../../../../../constants/pageSectionTypes";
import useEstimateWasteCalculator, {
	TrainingEfficiencyOptions,
	TrainingFrequencyOptions,
} from "../../../../../hooks/useEstimateWasteCalculator";
import MarkdownRenderer from "../../../../common/MarkdownRenderer/MarkdownRenderer";
import Button from "../../../../ui/Button/Button";
import Category from "../../../../ui/Category/Category";
import Lever from "../../../../ui/Lever/Lever";
import StackedBar from "../../../../ui/StackedBar/StackedBar";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import Typography from "../../../../ui/Typography/Typography";
import "./Calculator.scss";

type LeverConfiguration = {
	legend: string;
	options: string[];
	suffix?: string;
};

type LeversProps = {
	frequency: LeverConfiguration;
	efficiency: LeverConfiguration;
};

type CategoriesLabels = {
	baseline: string;
	avoidable: string;
};

type BarsLabels = {
	totalCost: string;
	totalWeeksToCompletion: string;
	hoursToCompletion: string;
};

type ResultsLabels = {
	label: string;
	unit: string;
	currencySymbol?: string;
	popover?: string;
};

type StackBarsProps = {
	categories: CategoriesLabels;
	bars: BarsLabels;
};

export type CalculatorContent = {
	enabled: boolean;
	title: string;
	content: string;
	explanation: IconTextList[];
	configuration: {
		levers: LeversProps;
		stackBars: StackBarsProps;
		results: {
			totalCost: ResultsLabels;
			avoidableCost: ResultsLabels;
			totalWeeksToCompletion: ResultsLabels;
			avoidableWeeksToCompletion: ResultsLabels;
		};
	};
};

const formatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

const formatAsCurrency = (amount: number) => formatter.format(amount);

type GetStackedBarDataProps = {
	title: string;
	totalValue: number;
	avoidableValue: number;
	formatFunction?: Function;
};
const getStackedBarData = ({
	title,
	totalValue,
	avoidableValue,
	formatFunction,
}: GetStackedBarDataProps) => ({
	title,
	maxValue: totalValue,
	bars: [
		{
			value: totalValue - avoidableValue,
			...(formatFunction ? { formatFunction: formatFunction } : {}),
		},
		{
			value: avoidableValue,
			...(formatFunction ? { formatFunction: formatFunction } : {}),
		},
	],
});

const iconsMap = {
	CheckCircle: CheckCircleOutlined,
	KeyboardDoubleArrowRight: KeyboardDoubleArrowRightOutlined,
	Bolt: BoltOutlined,
};

const Calculator = ({
	sectionContent,
}: PageSectionProps<CalculatorContent>) => {
	const [openTooltip, setOpenTooltip] = useState(false);
	const [selectedTrainingEfficiencyLabel, setSelectedTrainingEfficiencyLabel] =
		useState("pushed back" as TrainingEfficiencyOptions);
	const [selectedTrainingFrequencyLabel, setSelectedTrainingFrequencyLabel] =
		useState("1" as TrainingFrequencyOptions);
	const {
		totalCost,
		avoidableCost,
		totalWeeksToCompletion,
		avoidableWeeksToCompletion,
		totalHoursToCompletion,
		avoidableHoursToCompletion,
	} = useEstimateWasteCalculator({
		selectedTrainingEfficiencyLabel,
		selectedTrainingFrequencyLabel,
	});
	return (
		<section className="calculator background-container--gradient">
			<div className="content-container">
				<MarkdownRenderer>{sectionContent.title}</MarkdownRenderer>
				<div className="row row--column-start">
					<div className="column column--text-content">
						<MarkdownRenderer>{sectionContent.content}</MarkdownRenderer>
						<ul className="list list--icon-text">
							{sectionContent.explanation.map((item, index) => {
								const Icon = iconsMap[item.icon as keyof typeof iconsMap];

								return (
									<li className="list__item list__item--icon-text" key={index}>
										<Icon
											sx={{ fontSize: "2rem", color: "var(--brand-secondary)" }}
										/>
										<span className="list__item--icon-text__text">
											{item.text}
										</span>
									</li>
								);
							})}
						</ul>
					</div>
					<div className="column">
						<div className="levers row">
							{Object.entries(sectionContent.configuration.levers).map(
								([key, value]) => {
									const { legend, options, suffix = "" } = value;
									return (
										<Lever
											key={key}
											keyName={key}
											legend={legend}
											options={
												options as (
													| TrainingEfficiencyOptions
													| TrainingFrequencyOptions
												)[]
											}
											suffix={suffix}
											value={
												key === "frequency"
													? selectedTrainingFrequencyLabel
													: selectedTrainingEfficiencyLabel
											}
											onChange={
												(key === "frequency"
													? setSelectedTrainingFrequencyLabel
													: setSelectedTrainingEfficiencyLabel) as Dispatch<
													SetStateAction<
														TrainingEfficiencyOptions | TrainingFrequencyOptions
													>
												>
											}
										/>
									);
								},
							)}
						</div>
					</div>
				</div>
				<div className="row row--column-start calculator__results-and-stack-bars">
					<div className="column calculator__results">
						<Typography className="calculator__result">
							{sectionContent.configuration.results.totalCost.label}
							<span className="row">
								<Typography as="span" className="calculator__result-amount">
									{formatAsCurrency(totalCost)}{" "}
									{sectionContent.configuration.results.totalCost.unit}
								</Typography>
								<Button
									onClick={() => setOpenTooltip(!openTooltip)}
									className="calculator__result-popover">
									<QuestionMarkRounded
										sx={{ fontSize: "1.75rem", color: "var(--neutral-light)" }}
									/>
									{openTooltip && (
										<Tooltip
											content={
												sectionContent.configuration.results.totalCost
													.popover as string
											}
										/>
									)}
								</Button>
							</span>
						</Typography>
						<Typography className="calculator__result">
							{sectionContent.configuration.results.avoidableCost.label}
							<Typography as="span" className="calculator__result-amount">
								{formatAsCurrency(avoidableCost)}{" "}
								{sectionContent.configuration.results.avoidableCost.unit}
							</Typography>
						</Typography>
						<Typography className="calculator__result">
							{
								sectionContent.configuration.results.totalWeeksToCompletion
									.label
							}
							<Typography as="span" className="calculator__result-amount">
								{totalWeeksToCompletion}{" "}
								{
									sectionContent.configuration.results.totalWeeksToCompletion
										.unit
								}
							</Typography>
						</Typography>
						<Typography className="calculator__result">
							{
								sectionContent.configuration.results.avoidableWeeksToCompletion
									.label
							}
							<Typography as="span" className="calculator__result-amount">
								{avoidableWeeksToCompletion}{" "}
								{
									sectionContent.configuration.results
										.avoidableWeeksToCompletion.unit
								}
							</Typography>
						</Typography>
					</div>
					<div className="column">
						<div className="row calculator__categories">
							{Object.entries(
								sectionContent.configuration.stackBars.categories,
							).map(([key, value]) => {
								return (
									<Category key={key} className={`category--${key}`}>
										{value}
									</Category>
								);
							})}
						</div>
						<div className="column calculator__stack-bars">
							<StackedBar
								{...getStackedBarData({
									title: sectionContent.configuration.stackBars.bars.totalCost,
									totalValue: totalCost,
									avoidableValue: avoidableCost,
									formatFunction: formatAsCurrency,
								})}
							/>
							<StackedBar
								{...getStackedBarData({
									title:
										sectionContent.configuration.stackBars.bars
											.totalWeeksToCompletion,
									totalValue: totalWeeksToCompletion,
									avoidableValue: avoidableWeeksToCompletion,
								})}
							/>
							<StackedBar
								{...getStackedBarData({
									title:
										sectionContent.configuration.stackBars.bars
											.hoursToCompletion,
									totalValue: totalHoursToCompletion,
									avoidableValue: avoidableHoursToCompletion,
								})}
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Calculator;
